/**
 * ChatWidget — Floating coach chat button + panel for the learner app.
 *
 * Calls the native Scenario OS streaming chat endpoint directly:
 *   POST /scenario-os/chat/message/stream
 * with { domain, message, session_id, lesson_context }.
 *
 * The backend resolves which virtual coach agent handles the domain
 * (e.g. ai_orchestrator → AIDomainAgent) — the front-end
 * only needs to pass the domain. No platform token exchange required.
 *
 * Props:
 *   domain           {string}    Active learning domain ("ai_orchestrator", …)
 *   defaultOpen      {boolean}   Show panel immediately without clicking the FAB
 *   onClose          {function}  If provided, skip the FAB and render the panel directly
 *   lessonContext    {object}    Injected by the lesson page; forwarded to the backend
 *   panelMode        {string}    DockablePanel mode ("floating" | "docked")
 *   onPanelModeChange{function}  Called when the user changes the panel mode
 *   panelWidth       {number}    Initial panel width in px
 *   onPanelResize    {function}  Called when the user resizes the panel
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { DockablePanel } from '@shared/components/DockablePanel';
import ArtifactRenderer from '@shared/components/Chat/ArtifactRenderer';
import ReasoningPanel from '@shared/components/Reasoning/ReasoningPanel';
import { useReasoningStream } from '@shared/hooks/useReasoningStream';
import DraggableFab from '@shared/components/DraggableFab/DraggableFab';
import { decodeStoredReasoning, decodeStoredThinking } from '@shared/utils/thinkingTextDecoder';
import { buildLiveStatus } from '@shared/utils/liveStatus';
import { applyExpertEvent, expertHeading, expertBlockFromMessage } from '@shared/utils/expertStream';

const CHAT_STREAM_URL = '/scenario-os/chat/message/stream';
const CHAT_SESSIONS_URL = '/scenario-os/chat/sessions';

// The server stores (and the done payload carries) the raw stream of the model's cognitive__THINK call:
// `{"reasoning":"line\n..."}`.  Show the prose it wrote (same decoder as the portal and Studio chats); text that is
// not an envelope is returned untouched.
let _messageSeq = 0;
// Unique within the page even when several messages are created in the same millisecond.
const _nextMessageId = () => `${Date.now()}-${++_messageSeq}`;

const _decodeSteps = (steps) => (
  Array.isArray(steps) ? decodeStoredReasoning({ cot_steps: steps }).cot_steps : []
);

function _sessionStorageKey(domain, lessonContext) {
  const surface = lessonContext?.surface || 'default';
  return `scenario_os_chat_session:${domain || 'ai'}:${surface}`;
}

// ── HistoricalReasoningPanel ─────────────────────────────────────────────────
// Per-message details: steps, thinking, raw tokens, tools — all expandable.
function HistoricalReasoningPanel({
  cotSteps = [],
  reasoningSteps = [],
  thinkingText = '',
  rawTokens = [],
  toolActions = [],
}) {
  const [expanded, setExpanded] = useState(false);
  const [showThinking, setShowThinking] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [showTools, setShowTools] = useState(false);
  const stepCount = cotSteps.length + reasoningSteps.length;
  const tokenCount = (rawTokens || []).length;
  const toolCount = (toolActions || []).length;
  if (stepCount === 0 && !thinkingText && tokenCount === 0 && toolCount === 0) return null;

  const bits = [];
  if (stepCount) bits.push(`${stepCount} step${stepCount === 1 ? '' : 's'}`);
  if (toolCount) bits.push(`${toolCount} tool${toolCount === 1 ? '' : 's'}`);
  if (tokenCount) bits.push(`${tokenCount} raw`);

  return (
    <div style={{
      marginTop: 4,
      borderLeft: '2px solid #cbd5e1',
      paddingLeft: 8,
      fontSize: 12,
      color: '#64748b',
    }}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '2px 0',
          color: '#64748b',
          fontSize: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}
        title={expanded ? 'Hide details' : 'Show thinking, tools, raw tokens'}
      >
        {expanded ? '▾' : '▸'} Details{bits.length ? ` · ${bits.join(' · ')}` : ''}
      </button>
      {expanded && (
        <div style={{
          marginTop: 4,
          maxHeight: 360,
          overflowY: 'auto',
          paddingRight: 4,
        }}>
          {cotSteps.length > 0 && (
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Chain of Thought</div>
              {cotSteps.map((s, i) => (
                <div key={i} style={{
                  marginBottom: 6,
                  padding: '4px 6px',
                  background: '#f8fafc',
                  borderRadius: 4,
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.5,
                }}>
                  <span style={{ color: '#94a3b8', marginRight: 6 }}>Step {s.step}.</span>
                  {s.text}
                </div>
              ))}
            </div>
          )}
          {reasoningSteps.length > 0 && (
            <div style={{ marginTop: cotSteps.length > 0 ? 8 : 0 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Steps</div>
              {reasoningSteps.map((s, i) => {
                const text = typeof s === 'string' ? s :
                  (s.description || s.action || s.content || s.text || JSON.stringify(s));
                return (
                  <div key={i} style={{
                    marginBottom: 4,
                    padding: '3px 6px',
                    background: '#f1f5f9',
                    borderRadius: 4,
                    lineHeight: 1.5,
                  }}>
                    <span style={{ color: '#94a3b8', marginRight: 6 }}>{i + 1}.</span>
                    {text}
                    {s.tool_used && (
                      <span style={{ color: '#7c3aed', marginLeft: 6, fontSize: 11 }}>
                        [{s.tool_used}]
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {(thinkingText || tokenCount > 0) && (
            <div style={{ marginTop: 8 }}>
              <button
                type="button"
                onClick={() => setShowThinking((v) => !v)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#64748b', fontSize: 12, padding: 0, fontWeight: 600,
                }}
              >
                {showThinking ? '▾' : '▸'} Thinking
              </button>
              {showThinking && (
                <pre style={{
                  marginTop: 4, padding: 8, background: '#0f172a', color: '#e2e8f0',
                  borderRadius: 6, whiteSpace: 'pre-wrap', fontSize: 11, maxHeight: 160,
                  overflow: 'auto',
                }}>
                  {thinkingText || '(no assembled thinking text)'}
                </pre>
              )}
            </div>
          )}

          {tokenCount > 0 && (
            <div style={{ marginTop: 8 }}>
              <button
                type="button"
                onClick={() => setShowRaw((v) => !v)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#64748b', fontSize: 12, padding: 0, fontWeight: 600,
                }}
              >
                {showRaw ? '▾' : '▸'} Raw tokens ({tokenCount})
              </button>
              {showRaw && (
                <div style={{
                  marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4,
                  maxHeight: 140, overflow: 'auto',
                }}>
                  {rawTokens.map((tok, i) => (
                    <code
                      key={i}
                      title={`#${i + 1}`}
                      style={{
                        background: '#e2e8f0', color: '#0f172a', borderRadius: 4,
                        padding: '1px 4px', fontSize: 10,
                      }}
                    >
                      {JSON.stringify(tok)}
                    </code>
                  ))}
                </div>
              )}
            </div>
          )}

          {toolCount > 0 && (
            <div style={{ marginTop: 8 }}>
              <button
                type="button"
                onClick={() => setShowTools((v) => !v)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#64748b', fontSize: 12, padding: 0, fontWeight: 600,
                }}
              >
                {showTools ? '▾' : '▸'} Tools ({toolCount})
              </button>
              {showTools && toolActions.map((a, i) => (
                <div key={i} style={{
                  marginTop: 4, padding: '4px 6px', background: '#f8fafc',
                  borderRadius: 4, fontSize: 11,
                }}>
                  <strong>{a.tool}</strong> · {a.status}
                  {a.result != null && (
                    <div style={{ color: '#64748b', marginTop: 2 }}>
                      {typeof a.result === 'string'
                        ? a.result.slice(0, 120)
                        : JSON.stringify(a.result).slice(0, 120)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
  fab: {
    position: 'fixed', bottom: 24, right: 24,
    width: 52, height: 52, borderRadius: '50%',
    background: 'var(--color-primary, #1565c0)', color: '#fff',
    border: 'none', cursor: 'pointer', fontSize: 22,
    boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999, transition: 'transform 0.15s',
  },
  fabClose: {
    position: 'fixed', bottom: 24, right: 24,
    width: 52, height: 52, borderRadius: '50%',
    background: '#555', color: '#fff',
    border: 'none', cursor: 'pointer', fontSize: 20,
    boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999,
  },
  panelBody: {
    display: 'flex', flexDirection: 'column', height: '100%',
    background: '#fff', overflow: 'hidden',
  },
  messageList: {
    flex: 1, overflowY: 'auto', padding: '12px 16px',
    display: 'flex', flexDirection: 'column', gap: 10,
  },
  bubble: (role) => ({
    maxWidth: '85%',
    alignSelf: role === 'user' ? 'flex-end' : 'flex-start',
    background: role === 'user' ? 'var(--color-primary, #1565c0)' : '#f1f5f9',
    color: role === 'user' ? '#fff' : '#1e293b',
    borderRadius: role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
    padding: '10px 14px',
    fontSize: 13,
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  }),
  thinkingBubble: {
    alignSelf: 'flex-start',
    background: '#f1f5f9',
    borderRadius: '14px 14px 14px 4px',
    padding: '10px 14px',
    fontSize: 12,
    color: '#94a3b8',
    fontStyle: 'italic',
  },
  inputRow: {
    display: 'flex', gap: 8, padding: '10px 12px',
    borderTop: '1px solid #e2e8f0', background: '#fafbfc',
    alignItems: 'flex-end',
  },
  textarea: {
    flex: 1, resize: 'none', border: '1px solid #cbd5e1',
    borderRadius: 8, padding: '8px 12px',
    fontSize: 13, lineHeight: 1.5,
    fontFamily: 'inherit', outline: 'none',
    minHeight: 38, maxHeight: 120, overflowY: 'auto',
  },
  sendBtn: {
    background: 'var(--color-primary, #1565c0)', color: '#fff',
    border: 'none', borderRadius: 8, cursor: 'pointer',
    padding: '8px 14px', fontSize: 13, fontWeight: 600,
    whiteSpace: 'nowrap', alignSelf: 'flex-end',
    height: 38,
  },
  sendBtnDisabled: {
    background: '#cbd5e1', color: '#94a3b8',
    border: 'none', borderRadius: 8, cursor: 'not-allowed',
    padding: '8px 14px', fontSize: 13, fontWeight: 600,
    whiteSpace: 'nowrap', alignSelf: 'flex-end',
    height: 38,
  },
  errorBanner: {
    background: '#ffebee', color: '#c62828', padding: '8px 16px',
    fontSize: 12, lineHeight: 1.4,
  },
  emptyHint: {
    textAlign: 'center', color: '#94a3b8', fontSize: 12,
    padding: 16, marginTop: 'auto', marginBottom: 'auto',
  },
};

// ── Inline streaming chat panel ───────────────────────────────────────────────

function parseLaunchBlock(text) {
  if (!text || typeof text !== 'string') return null;
  const m = text.match(/```launch\s*([\s\S]*?)```/i);
  if (!m) return null;
  try {
    return JSON.parse(m[1].trim());
  } catch {
    return null;
  }
}

/** Normalize surface::OPEN SSE / legacy ```launch``` into a launch payload. */
function normalizeUiAction(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const path = raw.path || '';
  const type = raw.launch_type || raw.type || raw.surface_id || 'activity';
  return {
    ...raw,
    type,
    path,
    surface_id: raw.surface_id || '',
    require_confirm: raw.require_confirm !== false,
    label: raw.label || type,
  };
}

/** The expert's answer in its own block, labelled as the expert (never the coach's voice). */
function ExpertBlock({ block }) {
  return (
    <div data-testid="expert-block" style={{
      alignSelf: 'flex-start', background: '#eef6ff', border: '1px solid #bfdbfe', borderRadius: '14px 14px 14px 4px',
      padding: '8px 12px', marginBottom: 6, fontSize: 14, color: '#1e293b',
    }}>
      <div data-testid="expert-heading" style={{ fontSize: 12, fontWeight: 600, color: '#1d4ed8', marginBottom: 4 }}>
        {expertHeading(block)}
      </div>
      <div style={{ whiteSpace: 'pre-wrap' }}>{block.text}</div>
    </div>
  );
}

// What the coach is doing right now, while the turn runs: the latest step as a sentence, a one-line preview of the coach's own
// reasoning (the full text stays in "Coach details"), and the elapsed time. The timer ticks every second on the client, so the line
// moves even when the stream is silent (an expert call takes 20-60 s); the server heartbeat (`progress`) only raises it.
function LiveStatus({ steps, thinkingText, liveText, startedAt, serverElapsedS }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const clientS = startedAt ? Math.max(0, (now - startedAt) / 1000) : 0;
  const { label, preview, elapsedText } = buildLiveStatus({
    steps, thinkingText, liveText, elapsedS: Math.max(clientS, serverElapsedS || 0),
  });
  return (
    <span role="status" aria-live="polite" data-testid="live-status">
      <span style={{ color: '#64748b', fontStyle: 'italic' }}>{label}</span>
      <span style={{ color: '#94a3b8', marginLeft: 6, fontSize: 11 }}>{elapsedText}</span>
      {preview && (
        <span style={{ display: 'block', marginTop: 2, color: '#94a3b8', fontSize: 11, fontStyle: 'italic' }}>{preview}</span>
      )}
    </span>
  );
}

function ChatPanel({ domain, lessonContext, quickActions, onLaunch }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [pendingLaunch, setPendingLaunch] = useState(null);
  const sessionIdRef = useRef(null);
  const listRef = useRef(null);
  const abortRef = useRef(null);
  const timeoutRef = useRef(null);

  // ── CoT reasoning stream ──────────────────────────────────────────────────
  // useReasoningStream handles both new-style SSE (event: reasoning / done / tool_use)
  // and legacy `step` events translated via processSSELine('status', ...).
  const {
    reasoningSteps,
    toolActions,
    liveText,
    rawTokens,
    thinkingText,
    progressElapsedS,
    processSSELine,
    getTurnSnapshot,
    reset: resetReasoning,
  } = useReasoningStream();
  const turnStartedAtRef = useRef(0);

  const domainLabel = domain
    ? domain.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : 'Your';

  // Restore this learner's prior chat for the domain (own sessions only).
  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('scenario_os_token') || '';
    if (!token || !domain) return undefined;

    const storageKey = _sessionStorageKey(domain, lessonContext);
    const preferredId = sessionStorage.getItem(storageKey) || '';

    (async () => {
      try {
        const listRes = await fetch(CHAT_SESSIONS_URL, {
          headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
        });
        if (!listRes.ok || cancelled) return;
        const listData = await listRes.json();
        const sessions = Array.isArray(listData?.sessions) ? listData.sessions : [];
        // Only this learner's sessions for the active domain (API already scopes by learner).
        const domainSessions = sessions.filter(
          (s) => !s.domain || s.domain === domain || s.domain === 'ai',
        );
        if (!domainSessions.length) return;

        const match = preferredId
          ? domainSessions.find((s) => s.session_id === preferredId)
          : null;
        const chosen = match || domainSessions[0];
        if (!chosen?.session_id) return;

        const detailRes = await fetch(
          `${CHAT_SESSIONS_URL}/${encodeURIComponent(chosen.session_id)}`,
          { headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' } },
        );
        if (!detailRes.ok || cancelled) return;
        const detail = await detailRes.json();
        const rawMsgs = Array.isArray(detail?.messages) ? detail.messages : [];
        const hydrated = rawMsgs
          .filter((m) => m && (m.content || m.text))
          .map((m, i) => ({
            id: `${chosen.session_id}-${i}`,
            role: (m.role === 'learner' || m.role === 'user') ? 'user' : 'assistant',
            // what the learner saw of an expert's answer (a streamed expert block) is kept in the transcript as its own message
            ...(m.kind === 'expert' ? { expertBlock: expertBlockFromMessage(m) } : {}),
            content: m.content || m.text || '',
            streaming: false,
            cotSteps: _decodeSteps(m.cot_steps || m.cotSteps || []),
            reasoningSteps: _decodeSteps(m.reasoning_steps || m.reasoningSteps || []),
            thinkingText: decodeStoredThinking(m.thinking_text || m.thinkingText || ''),
            rawTokens: m.raw_tokens || m.rawTokens || [],
            toolActions: m.tool_actions || m.toolActions || [],
          }));
        if (cancelled || !hydrated.length) return;
        sessionIdRef.current = chosen.session_id;
        sessionStorage.setItem(storageKey, chosen.session_id);
        setMessages(hydrated);
      } catch {
        // History restore is best-effort; chat still works without it.
      }
    })();

    return () => { cancelled = true; };
  }, [domain, lessonContext?.surface]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = useCallback(async (overrideText) => {
    const text = (overrideText != null ? String(overrideText) : input).trim();
    if (!text || streaming) return;

    const token = localStorage.getItem('scenario_os_token') || '';
    if (!token) {
      setError('Not authenticated. Please log in again.');
      return;
    }

    // Cancel any in-flight request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setInput('');
    setError(null);
    setPendingLaunch(null);
    setStreaming(true);
    resetReasoning();
    turnStartedAtRef.current = Date.now();

    // Client-side fallback: if no "done" SSE arrives within the window, show
    // an error and re-enable the input.  Timeout is 90 s (was 30 s) to
    // accommodate Neo4j context retrieval (~10 s) + LLM streaming (~30-60 s).
    // The timer is also reset on every SSE chunk so intermittent activity
    // (CoT steps, partial text) keeps the window open.
    const CHAT_TIMEOUT_MS = 90000;
    const resetChatTimeout = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setStreaming(false);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsgId
              ? { ...m, content: "I'm having trouble answering right now — try again shortly.", streaming: false }
              : m,
          ),
        );
      }, CHAT_TIMEOUT_MS);
    };
    resetChatTimeout();

    // Both ids are taken now, from a counter, so they are always different.  (`Date.now()` read inside the state
    // updater could equal the assistant id, and every `m.id === assistantMsgId` update then rewrote the user's bubble.)
    const userMsgId = _nextMessageId();
    const assistantMsgId = _nextMessageId();

    // Optimistically add user message
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: text, id: userMsgId },
    ]);

    // Placeholder for streaming assistant reply
    setMessages((prev) => [
      ...prev,
      { role: 'assistant', content: '', streaming: true, id: assistantMsgId, artifact: null },
    ]);

    try {
      const res = await fetch(CHAT_STREAM_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          domain,
          message: text,
          session_id: sessionIdRef.current || undefined,
          lesson_context: lessonContext || undefined,
        }),
        signal: controller.signal,
      });

      if (!res.ok) {
        const errText = await res.text().catch(() => res.statusText);
        throw new Error(`${res.status}: ${errText}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finalReply = '';
      let currentEventType = '';
      let currentDataLine = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        // Reset the inactivity timeout on every chunk received so that
        // streaming CoT steps or partial text keep the window open.
        resetChatTimeout();
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // keep incomplete line

        for (const line of lines) {
          // ── Track SSE event type ─────────────────────────────────────────
          if (line.startsWith('event: ')) {
            currentEventType = line.slice(7).trim();
            continue;
          }

          // ── Blank line = end of SSE block ────────────────────────────────
          if (line === '') {
            if (currentDataLine && currentEventType) {
              // Dispatch to useReasoningStream for full CoT parsing
              processSSELine(currentEventType, currentDataLine);
            }
            currentEventType = '';
            currentDataLine = '';
            continue;
          }

          if (!line.startsWith('data: ')) continue;

          const raw = line.slice(6).trim();
          if (!raw || raw === '[DONE]') continue;
          currentDataLine = raw;

          try {
            const evt = JSON.parse(raw);
            // ── Session ID ──────────────────────────────────────────────────
            if (evt.session_id) {
              sessionIdRef.current = evt.session_id;
              try {
                sessionStorage.setItem(
                  _sessionStorageKey(domain, lessonContext),
                  evt.session_id,
                );
              } catch { /* ignore quota */ }
            }

            // ── Streaming reply tokens (arrive before `done`) ────────────────
            if (currentEventType === 'reply_delta' && evt.delta) {
              finalReply = (finalReply || '') + evt.delta;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsgId
                    ? { ...m, content: finalReply, streaming: true }
                    : m,
                ),
              );
            }

            // ── The expert's answer, live, in its own block ──────────────────
            if (currentEventType === 'expert') {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsgId ? { ...m, expert: applyExpertEvent(m.expert, evt) } : m,
                ),
              );
            }

            // ── Final reply (event: done OR old-style evt.reply) ─────────────
            if (currentEventType === 'done' || evt.reply != null) {
              clearTimeout(timeoutRef.current);
              const replyText = evt.reply ?? evt.text ?? finalReply ?? '';
              // Capture CoT steps and reasoning_trace steps from done payload
              // so they are shown in the per-message ReasoningPanel history view.
              const doneCotSteps = _decodeSteps(evt.cot_steps);
              const doneReasoningSteps = _decodeSteps(evt.reasoning_steps);
              const turnSnap = getTurnSnapshot();
              const histSteps = doneReasoningSteps.length
                ? doneReasoningSteps
                : (turnSnap.reasoningSteps || []).map((s) => s.text || s);
              if (replyText) {
                finalReply = replyText;
                // Markdown ```launch``` is fallback when surface::OPEN SSE was not used
                const launchFromFence = normalizeUiAction(parseLaunchBlock(replyText));
                if (launchFromFence) {
                  setPendingLaunch((prev) => prev || launchFromFence);
                }
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantMsgId
                      ? {
                          ...m,
                          content: replyText,
                          streaming: false,
                          cotSteps: doneCotSteps,
                          reasoningSteps: histSteps,
                          thinkingText: turnSnap.thinkingText || '',
                          rawTokens: turnSnap.rawTokens || [],
                          toolActions: turnSnap.toolActions || [],
                          launch: m.launch || launchFromFence || null,
                        }
                      : m,
                  ),
                );
              }
            }

            // ── Old-style step (no event: prefix) — route to reasoning hook ──
            if (!currentEventType && evt.step) {
              processSSELine('status', JSON.stringify({ step: evt.step }));
            }

            // ── UI surface action (surface::OPEN) ────────────────────────────
            if (currentEventType === 'ui_action' || evt.type === 'ui_action') {
              const action = normalizeUiAction(evt);
              if (action) {
                if (action.require_confirm === false && action.path && onLaunch) {
                  onLaunch(action);
                } else {
                  setPendingLaunch(action);
                }
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantMsgId ? { ...m, launch: action } : m,
                  ),
                );
              }
            }

            // ── Artifact ─────────────────────────────────────────────────────
            if (evt.type === 'artifact' || currentEventType === 'artifact') {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsgId
                    ? {
                        ...m,
                        artifact: {
                          type:      evt.artifact_type || evt.type_name || evt.type || 'unknown',
                          payload:   evt.payload   ?? {},
                          title:     evt.title     ?? '',
                          component: evt.component ?? '',
                        },
                      }
                    : m,
                ),
              );
            }
          } catch {
            // Non-JSON line, ignore
          }
        }
      }

      // Finalise the message if we got no explicit reply (fallback)
      if (!finalReply) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsgId ? { ...m, content: '…', streaming: false } : m,
          ),
        );
      }

      // Attach full turn CoT snapshot after SSE drain (refs are fully flushed).
      const turnSnap = getTurnSnapshot();
      if (
        turnSnap.thinkingText
        || turnSnap.rawTokens?.length
        || turnSnap.toolActions?.length
        || turnSnap.reasoningSteps?.length
      ) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsgId
              ? {
                  ...m,
                  thinkingText: turnSnap.thinkingText || m.thinkingText || '',
                  rawTokens: turnSnap.rawTokens?.length ? turnSnap.rawTokens : (m.rawTokens || []),
                  toolActions: turnSnap.toolActions?.length ? turnSnap.toolActions : (m.toolActions || []),
                  reasoningSteps: (m.reasoningSteps?.length
                    ? m.reasoningSteps
                    : (turnSnap.reasoningSteps || []).map((s) => s.text || s)),
                }
              : m,
          ),
        );
      }
    } catch (err) {
      clearTimeout(timeoutRef.current);
      if (err.name === 'AbortError') return;
      setError(err.message || 'Failed to reach the coaching service.');
      // Remove the empty streaming placeholder
      setMessages((prev) =>
        prev.filter((m) => m.id !== assistantMsgId),
      );
    } finally {
      clearTimeout(timeoutRef.current);
      setStreaming(false);
    }
  }, [input, streaming, domain, lessonContext, resetReasoning, processSSELine, getTurnSnapshot, onLaunch]);

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const noMessages = messages.length === 0;
  const isSessionHome = lessonContext?.surface === 'session_home' || lessonContext?.surface === 'agent_led';

  const hasReasoning = streaming
    || reasoningSteps.length > 0
    || liveText
    || thinkingText
    || rawTokens.length > 0
    || toolActions.length > 0;

  return (
    <div style={S.panelBody}>
      <div ref={listRef} style={S.messageList}>
        {noMessages && !streaming && (
          <div style={S.emptyHint}>
            {isSessionHome
              ? 'Say hi to start — or pick continue, quick review, or something new.'
              : `Ask your ${domainLabel} coach anything about this lesson.`}
          </div>
        )}
        {messages.map((msg) => (
          msg.expertBlock ? (
            <div key={msg.id} style={{ maxWidth: '95%', alignSelf: 'flex-start' }}><ExpertBlock block={msg.expertBlock} /></div>
          ) : (
          <div key={msg.id} style={{ maxWidth: '95%', alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
            {(msg.expert || []).map((b) => <ExpertBlock key={b.id} block={b} />)}
            <div style={S.bubble(msg.role)}>
              {/* While the turn runs: the latest step, a preview of the coach's reasoning and the elapsed time */}
              {msg.streaming && !msg.content
                ? (
                  <LiveStatus
                    steps={reasoningSteps}
                    thinkingText={thinkingText}
                    liveText={liveText}
                    startedAt={turnStartedAtRef.current}
                    serverElapsedS={progressElapsedS}
                  />
                )
                : msg.content || (msg.streaming ? '' : '')}
            </div>
            {msg.artifact && (
              <ArtifactRenderer
                type={msg.artifact.type}
                payload={msg.artifact.payload}
                title={msg.artifact.title}
                component={msg.artifact.component}
              />
            )}
            {/* ── Per-message historical CoT / Reasoning panel ──────────────── */}
            {/* Shown for completed assistant messages that have stored CoT data */}
            {msg.role === 'assistant' && !msg.streaming && (
              (msg.cotSteps?.length > 0
                || msg.reasoningSteps?.length > 0
                || msg.thinkingText
                || msg.rawTokens?.length > 0
                || msg.toolActions?.length > 0) && (
                <HistoricalReasoningPanel
                  cotSteps={msg.cotSteps || []}
                  reasoningSteps={msg.reasoningSteps || []}
                  thinkingText={msg.thinkingText || ''}
                  rawTokens={msg.rawTokens || []}
                  toolActions={msg.toolActions || []}
                />
              )
            )}
          </div>
          )
        ))}
      </div>

      {/* ── Agent Reasoning Panel ─────────────────────────────────────────── */}
      {hasReasoning && (
        <div style={{
          borderTop: '1px solid #e2e8f0',
          maxHeight: 320,
          overflowY: 'auto',
          flexShrink: 0,
        }}>
          <ReasoningPanel
            reasoningSteps={reasoningSteps}
            liveText={liveText}
            thinkingText={thinkingText}
            rawTokens={rawTokens}
            toolActions={toolActions}
            isStreaming={streaming}
            defaultOpen={false}
            title="Coach details"
          />
        </div>
      )}

      {error && <div style={S.errorBanner}>{error}</div>}

      {pendingLaunch && (
        <div style={{ padding: '8px 12px', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
          <button
            type="button"
            onClick={() => {
              onLaunch?.(pendingLaunch);
              setPendingLaunch(null);
            }}
            style={{
              width: '100%',
              border: 'none',
              borderRadius: 8,
              padding: '10px 12px',
              background: '#0f766e',
              color: '#fff',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {pendingLaunch.label
              ? `Open ${pendingLaunch.label}`
              : `Open recommended ${pendingLaunch.type || 'activity'}`}
          </button>
        </div>
      )}

      {Array.isArray(quickActions) && quickActions.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 12px 0' }}>
          {quickActions.map((a) => (
            <button
              key={a.label}
              type="button"
              disabled={streaming}
              onClick={() => sendMessage(a.message)}
              style={{
                border: '1px solid #cbd5e1',
                background: '#fff',
                borderRadius: 999,
                padding: '6px 10px',
                fontSize: 12,
                cursor: streaming ? 'default' : 'pointer',
                color: '#334155',
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}

      <div style={S.inputRow}>
        <textarea
          style={S.textarea}
          placeholder={isSessionHome ? 'Tell your coach how you want to learn…' : 'Ask your coach…'}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
          rows={1}
          disabled={streaming}
          aria-label="Chat message input"
        />
        <button
          style={streaming || !input.trim() ? S.sendBtnDisabled : S.sendBtn}
          onClick={() => sendMessage()}
          disabled={streaming || !input.trim()}
          aria-label="Send message"
        >
          {streaming ? '…' : 'Send'}
        </button>
      </div>
    </div>
  );
}

// ── ChatWidget ────────────────────────────────────────────────────────────────

export default function ChatWidget({
  domain,
  defaultOpen = false,
  onClose,
  lessonContext,
  panelMode,
  onPanelModeChange,
  panelWidth,
  onPanelResize,
  quickActions,
  onLaunch,
  title,
}) {
  const [open, setOpen] = useState(defaultOpen);

  const domainLabel = domain
    ? domain.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : 'Domain';

  // FAB — shown when the host didn't supply an onClose handler and the panel is closed
  if (!open && !onClose) {
    return (
      <DraggableFab
        storageKey="teachiq_chat_fab_pos"
        defaultPosition={{ bottom: 24, right: 24 }}
        onClick={() => setOpen(true)}
        title={`Ask your ${domainLabel} coach`}
        aria-label="Open coach chat"
        style={{
          width: 52, height: 52, borderRadius: '50%',
          background: 'var(--color-primary, #1565c0)', color: '#fff',
          border: 'none', fontSize: 22,
          boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'transform 0.15s',
        }}
      >
        💬
      </DraggableFab>
    );
  }

  const handleClose = () => {
    setOpen(false);
    onClose?.();
  };

  return (
    <>
      <DockablePanel
        title={title || `Ask your ${domainLabel} Coach`}
        mode={panelMode || 'floating'}
        onModeChange={onPanelModeChange}
        width={panelWidth || 380}
        onResize={onPanelResize}
        onClose={handleClose}
      >
        <ChatPanel
          domain={domain}
          lessonContext={lessonContext}
          quickActions={quickActions}
          onLaunch={onLaunch}
        />
      </DockablePanel>

      {!onClose && (
        <DraggableFab
          storageKey="teachiq_chat_fab_pos"
          defaultPosition={{ bottom: 24, right: 24 }}
          onClick={handleClose}
          aria-label="Close coach chat"
          style={{
            width: 52, height: 52, borderRadius: '50%',
            background: '#555', color: '#fff',
            border: 'none', fontSize: 20,
            boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          ✕
        </DraggableFab>
      )}
    </>
  );
}
